import $ from 'jquery';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import 'bootstrap';
import 'gasparesganga-jquery-loading-overlay';
import type {
    FingerprintResult,
    SearchData,
    SearchResult,
} from './Schema';
import {
    searchSchema,
    searchResultSchema,
} from './Schema';
import './search.css';

let searchData: SearchData | null = null;

// Parse and validate search view data from the <script type="application/json"> element
const parseSearchViewData = (): SearchData | null => {
    const scriptDataElem = document.querySelector<HTMLScriptElement>(
        'script[type="application/json"][data-view-data-search]',
    );
    if (!scriptDataElem?.textContent) {
        console.error('Search view data script element not found or empty');
        return null;
    }
    try {
        const validationResult = searchSchema.safeParse(JSON.parse(scriptDataElem.textContent));
        if (!validationResult.success) {
            console.error('Invalid search view data:', validationResult.error);
            return null;
        }
        scriptDataElem.remove();
        return validationResult.data;
    } catch (error) {
        console.error('Error parsing search view data:', error);
        return null;
    }
};

// Helper function to safely call jQuery plugins that may be loaded from CDN
const safeCallPlugin = <T extends JQuery>(
    element: T,
    pluginName: keyof JQuery,
    action: string | unknown
): boolean => {
    if (typeof element[pluginName] === 'function') {
        (element[pluginName] as (action: string | unknown) => JQuery)(action);
        return true;
    }
    return false;
};

// Initialize search functionality
const initializeSearch = (): void => {
    searchData = parseSearchViewData();

    $('#search_field').focus();

    const searchTerm = $('#search_field').val();
    if (searchTerm) {
        executeSearch();
    }

    loadFingerprint();
    initializePopover();
    initializeEventHandlers();
};

// Load fingerprint
const loadFingerprint = async (): Promise<void> => {
    if (!searchData) return;

    try {
        const fp = await FingerprintJS.load();
        const result = await fp.get() as FingerprintResult;
        searchData.visitorId = result.visitorId;
    } catch (error) {
        console.error('Failed to load fingerprint:', error);
    }
};

// Initialize popover
const initializePopover = (): void => {
    if (!searchData) return;

    $('.esPopover').popover({
        content: function() {
            // Clone the content to avoid removing it from the template
            return $('.popover-container').children().clone();
        },
        html: true,
        placement: 'bottom',
        container: 'body',
    });

    $('.esPopover').on('inserted.bs.popover', () => {
        updatePopoverContent();
    });

    $('.esPopover').on('shown.bs.popover', () => {
        handlePopoverShown();
    });

    $('.esPopover').on('hidden.bs.popover', () => {
        handlePopoverHidden();
    });

    setupKeyboardNavigation();
};

// Update popover content
const updatePopoverContent = (): void => {
    if (!searchData) return;

    $('.popover-title').addClass('text-center');

    if (searchData.searchFuzzy) {
        $('.es-search-type-fuzzy').addClass('active').attr('aria-pressed', 'true');
        $('.es-search-type-exact').removeClass('active').attr('aria-pressed', 'false');
    } else {
        $('.es-search-type-exact').addClass('active').attr('aria-pressed', 'true');
        $('.es-search-type-fuzzy').removeClass('active').attr('aria-pressed', 'false');
    }
};

// Initialize accessible dropdown (WAI-ARIA Combobox Pattern)
// Reference: https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/
const initializeAccessibleDropdown = (): void => {
    const $button = $('#schools-dropdown-button');
    const $listbox = $('#schools-listbox');

    if ($button.length === 0 || $listbox.length === 0) return;

    // Button click/keyboard handler (DOM focus stays on button)
    $button.on('click', handleDropdownButtonClick);
    $button.on('keydown', handleDropdownButtonKeydown);

    // Option click handler
    $listbox.on('click', '[role="option"]', handleOptionClick);

    // Close on outside click
    $(document).on('click', (event: JQuery.ClickEvent) => {
        const $target = $(event.target);
        if (!$target.closest('.schools-dropdown').length && $button.attr('aria-expanded') === 'true') {
            closeDropdownWithoutSelection();
        }
    });
};

// Handle button click
const handleDropdownButtonClick = (event: JQuery.ClickEvent): void => {
    event.preventDefault();
    event.stopPropagation();

    const $button = $('#schools-dropdown-button');
    const isExpanded = $button.attr('aria-expanded') === 'true';

    if (isExpanded) {
        closeDropdownWithoutSelection();
    } else {
        openDropdownWithoutSelection();
    }
};

// Handle button keyboard when closed
const handleClosedComboboxKeys = (event: JQuery.KeyDownEvent, key: string): void => {
    if (key === 'ArrowDown') {
        event.preventDefault();
        openDropdownWithoutSelection();
    } else if (key === 'ArrowUp') {
        event.preventDefault();
        openDropdownAndFocusFirst();
    } else if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        openDropdownWithoutSelection();
    } else if (key === 'Home') {
        event.preventDefault();
        openDropdownAndFocusFirst();
    } else if (key === 'End') {
        event.preventDefault();
        openDropdownAndFocusLast();
    }
};

// Handle button keyboard when open
const handleOpenComboboxKeys = (event: JQuery.KeyDownEvent, key: string): void => {
    const $listbox = $('#schools-listbox');
    const $options = $listbox.find('[role="option"]');

    if (key === 'ArrowDown') {
        event.preventDefault();
        moveVisualFocusNext();
    } else if (key === 'ArrowUp') {
        event.preventDefault();
        moveVisualFocusPrev();
    } else if (key === 'Home') {
        event.preventDefault();
        setVisualFocus($options.first());
    } else if (key === 'End') {
        event.preventDefault();
        setVisualFocus($options.last());
    } else if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        selectCurrentOption();
    } else if (key === 'Escape') {
        event.preventDefault();
        closeDropdownWithoutSelection();
    } else if (key === 'Tab') {
        selectCurrentOption();
    }
};

// Handle button keyboard (per W3C Select-Only Combobox Pattern)
// Reference: https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/
const handleDropdownButtonKeydown = (event: JQuery.KeyDownEvent): void => {
    const key = event.key;
    const $button = $('#schools-dropdown-button');
    const isExpanded = $button.attr('aria-expanded') === 'true';

    if (isExpanded) {
        handleOpenComboboxKeys(event, key);
    } else {
        handleClosedComboboxKeys(event, key);
    }
};

// Set visual focus via aria-activedescendant (DOM focus stays on button)
const setVisualFocus = ($option: JQuery): void => {
    const $button = $('#schools-dropdown-button');
    const $listbox = $('#schools-listbox');
    const optionId = $option.attr('id');

    if (optionId) {
        // Update aria-activedescendant on the combobox
        $button.attr('aria-activedescendant', optionId);

        // Update visual styling
        $listbox.find('[role="option"]').removeClass('focused');
        $option.addClass('focused');

        // Scroll into view if needed
        const optionElement = $option[0];
        if (optionElement) {
            optionElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }
};

// Move visual focus to next option
const moveVisualFocusNext = (): void => {
    const $button = $('#schools-dropdown-button');
    const $listbox = $('#schools-listbox');
    const $options = $listbox.find('[role="option"]');
    const currentId = $button.attr('aria-activedescendant');

    if (!currentId) {
        setVisualFocus($options.first());
        return;
    }

    const $current = $listbox.find(`#${currentId}`);
    const currentIndex = $options.index($current);
    const nextIndex = currentIndex + 1;

    if (nextIndex < $options.length) {
        setVisualFocus($options.eq(nextIndex));
    }
};

// Move visual focus to previous option
const moveVisualFocusPrev = (): void => {
    const $button = $('#schools-dropdown-button');
    const $listbox = $('#schools-listbox');
    const $options = $listbox.find('[role="option"]');
    const currentId = $button.attr('aria-activedescendant');

    if (!currentId) {
        setVisualFocus($options.first());
        return;
    }

    const $current = $listbox.find(`#${currentId}`);
    const currentIndex = $options.index($current);
    const prevIndex = currentIndex - 1;

    if (prevIndex >= 0) {
        setVisualFocus($options.eq(prevIndex));
    }
};

// Select the currently focused option (via aria-activedescendant)
const selectCurrentOption = (): void => {
    if (!searchData) return;

    const $button = $('#schools-dropdown-button');
    const $listbox = $('#schools-listbox');
    const currentId = $button.attr('aria-activedescendant');

    if (!currentId) return;

    const $currentOption = $listbox.find(`#${currentId}`);
    if ($currentOption.length === 0) return;

    const selectedText = $currentOption.text().trim();
    const selectedValue = $currentOption.data('value') as string;

    // Update search data
    searchData.searchSite = String(selectedValue);

    // Update button text
    $('#change_school').text(selectedText);

    // Update aria-selected on all options
    const $allOptions = $listbox.find('[role="option"]');
    $allOptions.attr('aria-selected', 'false');
    $currentOption.attr('aria-selected', 'true');

    // Close dropdown and return focus to button
    closeDropdownAfterSelection();
};

// Handle option click
const handleOptionClick = function (this: HTMLElement, event: JQuery.ClickEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const $option = $(this);
    setVisualFocus($option);
    selectCurrentOption();
};

// Open dropdown without changing selection
const openDropdownWithoutSelection = (): void => {
    const $button = $('#schools-dropdown-button');
    const $listbox = $('#schools-listbox');

    // Update ARIA state
    $button.attr('aria-expanded', 'true');

    // Show dropdown
    $listbox.addClass('open').css('display', 'block');

    // Set visual focus to selected option (don't move it)
    const $selectedOption = $listbox.find('[aria-selected="true"]');
    if ($selectedOption.length) {
        setVisualFocus($selectedOption);
    }
};

// Open dropdown and move visual focus to first option
const openDropdownAndFocusFirst = (): void => {
    const $button = $('#schools-dropdown-button');
    const $listbox = $('#schools-listbox');
    const $options = $listbox.find('[role="option"]');

    // Update ARIA state
    $button.attr('aria-expanded', 'true');

    // Show dropdown
    $listbox.addClass('open').css('display', 'block');

    // Move visual focus to first option
    setVisualFocus($options.first());
};

// Open dropdown and move visual focus to last option
const openDropdownAndFocusLast = (): void => {
    const $button = $('#schools-dropdown-button');
    const $listbox = $('#schools-listbox');
    const $options = $listbox.find('[role="option"]');

    // Update ARIA state
    $button.attr('aria-expanded', 'true');

    // Show dropdown
    $listbox.addClass('open').css('display', 'block');

    // Move visual focus to last option
    setVisualFocus($options.last());
};

// Close dropdown after selection (returns focus to button)
const closeDropdownAfterSelection = (): void => {
    const $button = $('#schools-dropdown-button');
    const $listbox = $('#schools-listbox');
    const $options = $listbox.find('[role="option"]');

    // Update ARIA state
    $button.attr('aria-expanded', 'false');

    // Remove aria-activedescendant when closed
    $button.removeAttr('aria-activedescendant');

    // Remove visual focus styling
    $options.removeClass('focused');

    // Hide dropdown
    $listbox.removeClass('open').css('display', 'none');

    // DOM focus stays on button (already there)
    $button.focus();
};

// Close dropdown without selection (Escape key)
const closeDropdownWithoutSelection = (): void => {
    const $button = $('#schools-dropdown-button');
    const $listbox = $('#schools-listbox');
    const $options = $listbox.find('[role="option"]');

    // Update ARIA state
    $button.attr('aria-expanded', 'false');

    // Remove aria-activedescendant when closed
    $button.removeAttr('aria-activedescendant');

    // Remove visual focus styling
    $options.removeClass('focused');

    // Hide dropdown
    $listbox.removeClass('open').css('display', 'none');

    // DOM focus stays on button (already there)
    $button.focus();
};

// Initialize event handlers
const initializeEventHandlers = (): void => {
    $(document).on('click keydown', '.es-search-type-exact', handleExactSearchClick);
    $(document).on('click keydown', '.es-search-type-fuzzy', handleFuzzySearchClick);

    $('#search_button').on('click', handleSearchButtonClick);
    $('#search_field').on('keypress', handleSearchFieldKeypress);

    // Initialize accessible dropdown
    initializeAccessibleDropdown();
};

// Event handlers
const handleExactSearchClick = (event: JQuery.TriggeredEvent): void => {
    if (!searchData) return;

    const keyEvent = event as JQuery.KeyDownEvent;
    if (event.type === 'keydown' && keyEvent.key !== 'Enter' && keyEvent.key !== ' ') {
        return;
    }

    if (event.type === 'keydown') {
        event.preventDefault();
    }

    searchData.searchFuzzy = false;
    $('.es-search-type-exact').addClass('active').attr('aria-pressed', 'true');
    $('.es-search-type-fuzzy').removeClass('active').attr('aria-pressed', 'false');
};

const handleFuzzySearchClick = (event: JQuery.TriggeredEvent): void => {
    if (!searchData) return;

    const keyEvent = event as JQuery.KeyDownEvent;
    if (event.type === 'keydown' && keyEvent.key !== 'Enter' && keyEvent.key !== ' ') {
        return;
    }

    if (event.type === 'keydown') {
        event.preventDefault();
    }

    searchData.searchFuzzy = true;
    $('.es-search-type-fuzzy').addClass('active').attr('aria-pressed', 'true');
    $('.es-search-type-exact').removeClass('active').attr('aria-pressed', 'false');
};

const handleSearchButtonClick = (event: JQuery.ClickEvent): void => {
    event.preventDefault();
    executeSearch();
};

const handleSearchFieldKeypress = (event: JQuery.KeyPressEvent): void => {
    if (event.which === 13) {
        event.preventDefault();
        $('#search_button').trigger('click');
    }
};

// Close popover on outside click
const handlePopoverOutsideClick = (event: JQuery.ClickEvent): void => {
    $('.esPopover').each(function () {
        const describedBy = $(this).attr('aria-describedby');
        if (!describedBy) return;

        const isClickInsidePopover = $(event.target).closest('.popover-content').length > 0;
        const isClickOnButton = $(event.target).attr('aria-describedby') === describedBy;
        const isClickOnButtonChild =
            $(event.target).closest(`[aria-describedby="${describedBy}"]`).length > 0;

        if (!isClickInsidePopover && !isClickOnButton && !isClickOnButtonChild) {
            $(`[aria-describedby="${describedBy}"]`).trigger('click');
        }
    });
};

// Setup keyboard navigation for popover
const setupKeyboardNavigation = (): void => {
    $('.esPopover').on('keydown', (event: JQuery.KeyDownEvent) => {
        const key = event.key;

        if (key === 'Enter' || key === ' ') {
            event.preventDefault();
            const $popover = $(event.currentTarget);
            $popover.trigger('click');
        }
    });

    $(document).on('keydown', (event: JQuery.KeyDownEvent) => {
        if (event.key === 'Escape') {
            const $popover = $('.esPopover');
            if ($popover.attr('aria-expanded') === 'true') {
                event.preventDefault();
                $popover.popover('hide');
                $popover.focus();
            }
        }
    });
};

// Handle popover shown event
const handlePopoverShown = (): void => {
    $('.esPopover').attr('aria-expanded', 'true');

    // Associate the trigger with the live popover via aria-controls. Bootstrap
    // generates the popover element (and its id) at show time and mirrors that id
    // onto the trigger's aria-describedby, so reuse it here.
    $('.esPopover').each(function () {
        const popoverId = $(this).attr('aria-describedby');
        if (popoverId) {
            $(this).attr('aria-controls', popoverId);
        }
    });

    const $exactButton = $('.popover-content').find('.es-search-type-exact');
    const $fuzzyButton = $('.popover-content').find('.es-search-type-fuzzy');

    if ($exactButton.length) {
        setTimeout(() => {
            $exactButton.focus();
        }, 100);
    }

    // Remove any previously attached handlers before re-binding to prevent accumulation
    $fuzzyButton.off('keydown.esPopover');
    $exactButton.off('keydown.esPopover');

    // Handle Tab navigation from fuzzy button to next element
    $fuzzyButton.on('keydown.esPopover', (event: JQuery.KeyDownEvent) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            const $popover = $('.esPopover');

            // Wait for popover to fully hide before moving focus
            $popover.one('hidden.bs.popover', () => {
                // Reset Bootstrap's internal click state
                const popoverInstance = $popover.data('bs.popover');
                if (popoverInstance && popoverInstance.inState) {
                    popoverInstance.inState.click = false;
                }

                const $schoolsButton = $('#schools-dropdown-button');
                if ($schoolsButton.length) {
                    $schoolsButton.focus();
                } else {
                    $('#search_field').focus();
                }
            });

            $popover.popover('hide');
        }
    });

    // Handle Shift+Tab from exact button to gear icon
    $exactButton.on('keydown.esPopover', (event: JQuery.KeyDownEvent) => {
        if (event.key === 'Tab' && event.shiftKey) {
            event.preventDefault();
            const $popover = $('.esPopover');

            // Wait for popover to fully hide before moving focus
            $popover.one('hidden.bs.popover', () => {
                // Reset Bootstrap's internal click state
                const popoverInstance = $popover.data('bs.popover');
                if (popoverInstance && popoverInstance.inState) {
                    popoverInstance.inState.click = false;
                }

                $popover.focus();
            });

            $popover.popover('hide');
        }
    });
};

// Handle popover hidden event
const handlePopoverHidden = (): void => {
    $('.esPopover').attr('aria-expanded', 'false');
    // Remove aria-controls so it never references the now-detached popover element.
    $('.esPopover').removeAttr('aria-controls');
};

// Setup global handlers
$('html').on('click', handlePopoverOutsideClick);

// Pagination function
const paginate = (index: number): void => {
    const pages = window.pages as SearchResult[][];
    if (!pages) return;

    addResults(pages[index] as SearchResult[]);
    $('#pages li').removeClass('active');
    $(`#page${index}`).addClass('active');
    window.scrollTo(0, 0);
};

// Expose functions to window
window.search = { paginate };

// Issue the actual search request.
const performSearchRequest = (): void => {
    if (!searchData) return;

    const searchTerm = $('#search_field').val() as string;
    const requestData = {
        term: searchTerm,
        id: searchData.searchSite,
        fuzzy: searchData.searchFuzzy,
        visitorId: searchData.visitorId,
    };

    $.getJSON('/includes/actions/search.php', requestData)
        .done(handleSearchSuccess)
        .fail(handleSearchError);
};

// Execute search
const executeSearch = (): void => {
    if (!searchData) return;

    const resultsElement = $('#results');
    resultsElement.empty();

    // Show loading overlay if available (loaded from CDN)
    safeCallPlugin(resultsElement, 'LoadingOverlay', 'show');

    $('#pages').empty();
    $('#pages_container').hide();
    searchData.showCampus = searchData.searchSite.indexOf(',') !== -1;

    performSearchRequest();
};

// Handle search success
const handleSearchSuccess = (result: SearchResult[]): void => {
    const results = searchResultSchema.safeParse(result);
    if (!results.success) {
        console.error('Invalid search result', results.error);
        return;
    }
    const resultsElement = $('#results') as JQuery<HTMLElement>;

    // Hide loading overlay if available (loaded from CDN)
    safeCallPlugin(resultsElement, 'LoadingOverlay', 'hide');

    if (!result || result.length === 0) {
        resultsElement.append('No results have been found.');
        return;
    }

    if (result.length > 10) {
        paginateResults(result);
    } else {
        addResults(result);
    }
};

// Paginate results
const paginateResults = (result: SearchResult[]): void => {
    window.pages = [];
    const pages = window.pages;

    while (result.length >= 10) {
        pages.push(result.splice(0, 10));
    }

    if (result.length > 0) {
        pages.push(result);
    }

    if (pages.length > 1) {
        pages.forEach((_, index) => {
            $('#pages').append(
                `<li onclick="window.search.paginate(${index})" id="page${index}"><a>${index + 1}</a></li>`
            );
        });
        $('#pages_container').show();
    }

    paginate(0);
};

// Handle search error
const handleSearchError = (jqXHR: JQuery.jqXHR): void => {
    const resultsElement = $('#results');

    // Hide loading overlay if available (loaded from CDN)
    safeCallPlugin(resultsElement, 'LoadingOverlay', 'hide');

    if (jqXHR.status === 429) {
        displayRateLimitError(jqXHR);
    } else {
        resultsElement.append('An error occurred while searching. Please try again later.');
    }
};

// Display rate limit error
const displayRateLimitError = (jqXHR: JQuery.jqXHR): void => {
    const clone = $('.rate-limit-container').clone() as JQuery<HTMLElement>;
    clone.show();
    $('#results').append(clone);

    const retryAfter = jqXHR.getResponseHeader('Retry-After');
    if (retryAfter) {
        $('.retry-after-time', clone).text(retryAfter);
    }
};

const getResultsTemplate = (): HTMLTemplateElement =>
    document.getElementById('search-results-template') as HTMLTemplateElement;

// Add results to DOM
const addResults = (results: SearchResult[]): void => {
    if (!searchData) return;

    const resultsContainer = $('#results') as JQuery<HTMLElement>;
    resultsContainer.empty();

    if (!Array.isArray(results)) {
        console.error('Results is not an array', results);
        return;
    }

    if (results.length === 0) return;

    const list = getResultsTemplate()
        .content.querySelector('.ss-search-results-list')!
        .cloneNode(false) as HTMLUListElement;

    results.forEach((result: SearchResult) => list.appendChild(createResultRow(result)[0]));

    resultsContainer.append(list);
    resultsContainer.append('<hr>');
};

// Create result row
const createResultRow = (result: SearchResult): JQuery<HTMLElement> => {
    if (!searchData) {
        return $('<div></div>');
    }

    const title = result.title.replace(/"/g, '');
    let content = result.content.replace(/"/g, '');

    if (searchData.emailDisabled) {
        content = content.replace(/[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+/gm, '');
    }

    content = content.length <= 4 ? 'No description' : content;

    const link = result.link;
    const campus = result.campus;
    const showLink = link.length > 70 ? `${link.substring(0, 70)}...` : link;

    const rowNode = getResultsTemplate()
        .content.querySelector('.result-row')!
        .cloneNode(true) as HTMLElement;
    const resultRow = $(rowNode) as JQuery<HTMLElement>;

    resultRow.find('[data-title]').html(title);
    resultRow.find('[data-campus-name]').html(campus);
    resultRow.find('[data-url]').html(showLink);
    resultRow.find('[data-content]').html(content);
    resultRow.find('.result-link').attr('href', link);
    resultRow.find('.result-link').attr('title', link);

    if (searchData.showCampus) {
        resultRow.find('.row.campus').show();
    } else {
        resultRow.find('.row.campus').hide();
    }

    return resultRow;
};

// Initialize on document ready
$(() => {
    initializeSearch();
});
